/**
 * Preset Preview Button Component
 * Plays a 5-second audio preview of a preset's sound
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Loader2, Volume2 } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PresetPreviewButton = ({ 
  presetId, 
  size = 'sm',
  variant = 'ghost',
  className = '',
  onPlayStart,
  onPlayEnd 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, []);
  
  const handlePlay = useCallback(async () => {
    if (isPlaying) {
      // Stop playing
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
      setProgress(0);
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      onPlayEnd?.();
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create audio element if needed
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }
      
      // Set source to preview endpoint
      audioRef.current.src = `${API_URL}/api/guitar-presets/${presetId}/preview`;
      
      // Wait for audio to load
      await new Promise((resolve, reject) => {
        audioRef.current.oncanplaythrough = resolve;
        audioRef.current.onerror = reject;
        audioRef.current.load();
      });
      
      // Play audio
      await audioRef.current.play();
      setIsPlaying(true);
      setIsLoading(false);
      onPlayStart?.();
      
      // Track progress
      progressIntervalRef.current = setInterval(() => {
        if (audioRef.current) {
          const prog = (audioRef.current.currentTime / audioRef.current.duration) * 100;
          setProgress(prog);
        }
      }, 100);
      
      // Handle end
      audioRef.current.onended = () => {
        setIsPlaying(false);
        setProgress(0);
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
        onPlayEnd?.();
      };
      
    } catch (error) {
      console.error('Failed to play preview:', error);
      setIsLoading(false);
      setIsPlaying(false);
    }
  }, [presetId, isPlaying, onPlayStart, onPlayEnd]);
  
  // Compact icon button
  if (size === 'icon') {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePlay();
        }}
        disabled={isLoading}
        className={`relative p-2 rounded-full bg-violet-500/20 hover:bg-violet-500/40 transition-colors ${className}`}
        title="Preview preset sound"
      >
        {isLoading ? (
          <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-4 h-4 text-violet-400" />
        ) : (
          <Play className="w-4 h-4 text-violet-400" />
        )}
        
        {/* Progress ring */}
        {isPlaying && (
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-violet-500/30"
            />
            <circle
              cx="50%"
              cy="50%"
              r="45%"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray={`${progress * 2.83} 283`}
              className="text-violet-400"
            />
          </svg>
        )}
      </button>
    );
  }
  
  // Full button with label
  return (
    <Button
      variant={variant}
      size={size}
      onClick={(e) => {
        e.stopPropagation();
        handlePlay();
      }}
      disabled={isLoading}
      className={`relative overflow-hidden ${className}`}
    >
      {/* Progress background */}
      {isPlaying && (
        <motion.div
          className="absolute inset-0 bg-violet-500/20"
          initial={{ width: '0%' }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.1 }}
        />
      )}
      
      <span className="relative flex items-center gap-1.5">
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : isPlaying ? (
          <Pause className="w-3.5 h-3.5" />
        ) : (
          <Volume2 className="w-3.5 h-3.5" />
        )}
        <span className="text-xs">
          {isLoading ? 'Loading...' : isPlaying ? 'Stop' : 'Preview'}
        </span>
      </span>
    </Button>
  );
};

// Inline preview player with waveform visualization
export const PresetPreviewPlayer = ({ presetId, presetName }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(5);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  
  const drawWaveform = useCallback(() => {
    if (!canvasRef.current || !analyserRef.current || !isPlaying) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const analyser = analyserRef.current;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);
      
      ctx.fillStyle = '#18181b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / bufferLength) * 2.5;
      let x = 0;
      
      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height * 0.8;
        ctx.fillStyle = `rgba(139, 92, 246, ${0.5 + (dataArray[i] / 255) * 0.5})`;
        ctx.fillRect(x, canvas.height - barHeight, barWidth - 1, barHeight);
        x += barWidth;
      }
    };
    
    draw();
  }, [isPlaying]);
  
  useEffect(() => {
    if (isPlaying) {
      drawWaveform();
    } else {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    }
  }, [isPlaying, drawWaveform]);
  
  // Cleanup
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);
  
  const handlePlay = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      setProgress(0);
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Initialize audio context
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 256;
      }
      
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      // Fetch audio
      const response = await fetch(`${API_URL}/api/guitar-presets/${presetId}/preview`);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Create source
      const source = audioContextRef.current.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);
      
      setDuration(audioBuffer.duration);
      source.start(0);
      setIsPlaying(true);
      setIsLoading(false);
      
      // Track progress
      const startTime = audioContextRef.current.currentTime;
      const updateProgress = () => {
        if (!isPlaying) return;
        const elapsed = audioContextRef.current.currentTime - startTime;
        setProgress((elapsed / audioBuffer.duration) * 100);
        if (elapsed < audioBuffer.duration) {
          requestAnimationFrame(updateProgress);
        }
      };
      updateProgress();
      
      source.onended = () => {
        setIsPlaying(false);
        setProgress(0);
      };
      
    } catch (error) {
      console.error('Preview error:', error);
      setIsLoading(false);
    }
  };
  
  return (
    <div className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4">
      <div className="flex items-center gap-3 mb-3">
        <button
          onClick={handlePlay}
          disabled={isLoading}
          className="w-10 h-10 rounded-full bg-violet-500 hover:bg-violet-400 flex items-center justify-center transition-colors"
        >
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white ml-0.5" />
          )}
        </button>
        
        <div className="flex-1">
          <p className="text-sm font-medium text-white">{presetName || 'Preset Preview'}</p>
          <p className="text-xs text-zinc-500">
            {isPlaying ? `${Math.floor(progress * duration / 100)}s / ${Math.floor(duration)}s` : '5 second preview'}
          </p>
        </div>
      </div>
      
      {/* Waveform visualization */}
      <div className="relative h-12 bg-zinc-900 rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          width={300}
          height={48}
          className="w-full h-full"
        />
        
        {/* Progress bar */}
        <div 
          className="absolute bottom-0 left-0 h-1 bg-violet-500 transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default PresetPreviewButton;
